import React, { useEffect, useId, useMemo, useRef, useState } from 'react';
import { useT } from '../../../i18n';
import { formatPrice } from '../utils/roundMath';

const UP = '#34d399';
const DOWN = '#fb7185';
const PAD = { top: 18, right: 108, bottom: 26, left: 8 };

const clock = (seconds) =>
  new Date(seconds * 1000).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit', second: '2-digit' });
const axisPrice = (value) => `$${value.toLocaleString('en-US', { maximumFractionDigits: 0 })}`;

/** Width of the element, following resizes */
const useWidth = () => {
  const ref = useRef(null);
  const [width, setWidth] = useState(0);
  useEffect(() => {
    if (!ref.current) return undefined;
    const observer = new ResizeObserver(([entry]) => setWidth(entry.contentRect.width));
    observer.observe(ref.current);
    return () => observer.disconnect();
  }, []);
  return [ref, width];
};

/** Price pill on the right edge of the plot */
const Tag = ({ x, y, fill, color, children }) => (
  <g transform={`translate(${x},${y})`}>
    <rect x="0" y="-11" width={PAD.right - 12} height="22" rx="7" fill={fill} />
    <text x={(PAD.right - 12) / 2} y="4" textAnchor="middle" fontSize="11" fontWeight="700" fill={color} className="lb-num">
      {children}
    </text>
  </g>
);

/**
 * The live round on a time axis from its start to its close: the price line against the start price, green
 * where UP is winning and red where DOWN is, with the time left as empty space on the right
 */
const RoundChart = ({ points, lock, start, end, height = 260 }) => {
  const t = useT();
  const id = useId().replace(/:/g, '');
  const [ref, width] = useWidth();

  const geometry = useMemo(() => {
    if (!width || !lock || !start || points.length < 1) return null;
    const last = points[points.length - 1];
    const domainEnd = Math.max(end || 0, last.t, start + 1);
    const values = [...points.map((point) => point.p), lock];
    const min = Math.min(...values);
    const max = Math.max(...values);
    const span = max - min || lock * 0.0004 || 1;
    const lo = min - span * 0.25;
    const hi = max + span * 0.25;
    const plotW = width - PAD.left - PAD.right;
    const plotH = height - PAD.top - PAD.bottom;
    const x = (time) => PAD.left + ((Math.min(Math.max(time, start), domainEnd) - start) / (domainEnd - start)) * plotW;
    const y = (price) => PAD.top + ((hi - price) / (hi - lo)) * plotH;
    const line = points.map((point) => [x(point.t), y(point.p)]);
    const lockY = y(lock);
    const path = line.map(([px, py]) => `${px.toFixed(1)},${py.toFixed(1)}`).join(' ');
    const area = `${line[0][0].toFixed(1)},${lockY.toFixed(1)} ${path} ${line[line.length - 1][0].toFixed(1)},${lockY.toFixed(1)}`;
    const ticks = [0.15, 0.5, 0.85].map((f) => hi - (hi - lo) * f);
    // keep the two pills apart when the price sits near the start price
    let lastTagY = line[line.length - 1][1];
    if (Math.abs(lastTagY - lockY) < 24) lastTagY = lockY + (last.p >= lock ? -24 : 24);
    return { plotW, plotH, x, y, path, area, lockY, ticks, last, lastXY: line[line.length - 1], lastTagY, domainEnd };
  }, [points, lock, start, end, width, height]);

  const side = geometry ? (geometry.last.p > lock ? 'up' : geometry.last.p < lock ? 'down' : null) : null;
  const color = side === 'down' ? DOWN : UP;
  const right = width - PAD.right;

  return (
    <div ref={ref} className="w-full" style={{ height }}>
      {geometry ? (
        <svg width={width} height={height} className="block overflow-visible" role="img" aria-label={t('bvb.chartLabel')}>
          <defs>
            <clipPath id={`above-${id}`}>
              <rect x="0" y="0" width={width} height={geometry.lockY} />
            </clipPath>
            <clipPath id={`below-${id}`}>
              <rect x="0" y={geometry.lockY} width={width} height={height} />
            </clipPath>
            <linearGradient id={`up-${id}`} x1="0" y1="0" x2="0" y2="1">
              <stop offset="0%" stopColor={UP} stopOpacity="0.32" />
              <stop offset="100%" stopColor={UP} stopOpacity="0.04" />
            </linearGradient>
            <linearGradient id={`down-${id}`} x1="0" y1="1" x2="0" y2="0">
              <stop offset="0%" stopColor={DOWN} stopOpacity="0.32" />
              <stop offset="100%" stopColor={DOWN} stopOpacity="0.04" />
            </linearGradient>
          </defs>

          {/* price grid */}
          {geometry.ticks.map((value) => {
            const ty = geometry.y(value);
            // the axis label gives way to the two price pills
            const covered = [geometry.lockY, geometry.lastTagY].some((tagY) => Math.abs(tagY - ty) < 18);
            return (
              <g key={value}>
                <line x1={PAD.left} x2={right} y1={ty} y2={ty} stroke="rgba(255,255,255,0.05)" />
                {!covered && (
                  <text x={right + 8} y={ty + 4} fontSize="10" fill="rgba(255,255,255,0.3)" className="lb-num">
                    {axisPrice(value)}
                  </text>
                )}
              </g>
            );
          })}

          {/* UP above the start price, DOWN below */}
          <polygon points={geometry.area} fill={`url(#up-${id})`} clipPath={`url(#above-${id})`} />
          <polygon points={geometry.area} fill={`url(#down-${id})`} clipPath={`url(#below-${id})`} />
          <polyline points={geometry.path} fill="none" stroke={UP} strokeWidth="2.2" strokeLinejoin="round" clipPath={`url(#above-${id})`} />
          <polyline points={geometry.path} fill="none" stroke={DOWN} strokeWidth="2.2" strokeLinejoin="round" clipPath={`url(#below-${id})`} />

          {/* start price */}
          <line x1={PAD.left} x2={right} y1={geometry.lockY} y2={geometry.lockY} stroke="rgba(255,255,255,0.45)" strokeDasharray="5 5" />
          <Tag x={right + 6} y={geometry.lockY} fill="rgba(255,255,255,0.12)" color="rgba(255,255,255,0.85)">
            {formatPrice(lock)}
          </Tag>

          {/* now */}
          <line
            x1={geometry.lastXY[0]}
            x2={geometry.lastXY[0]}
            y1={PAD.top}
            y2={height - PAD.bottom}
            stroke="rgba(255,255,255,0.08)"
          />
          <circle cx={geometry.lastXY[0]} cy={geometry.lastXY[1]} r="4" fill={color}>
            <animate attributeName="r" values="4;9;4" dur="1.8s" repeatCount="indefinite" />
            <animate attributeName="opacity" values="1;0.25;1" dur="1.8s" repeatCount="indefinite" />
          </circle>
          <circle cx={geometry.lastXY[0]} cy={geometry.lastXY[1]} r="3.5" fill={color} />
          <Tag x={right + 6} y={geometry.lastTagY} fill={color} color="#04130d">
            {formatPrice(geometry.last.p)}
          </Tag>

          {/* time axis: the round from its start to its close */}
          <text x={PAD.left} y={height - 8} fontSize="10" fill="rgba(255,255,255,0.35)" className="lb-num">
            {t('bvb.chartStart')} {clock(start)}
          </text>
          <text x={right} y={height - 8} textAnchor="end" fontSize="10" fill="rgba(255,255,255,0.35)" className="lb-num">
            {t('bvb.chartEnd')} {clock(geometry.domainEnd)}
          </text>
          <text x={right + 8} y={geometry.lockY + (geometry.lastTagY < geometry.lockY ? 24 : -16)} fontSize="9" fill="rgba(255,255,255,0.4)">
            {t('bvb.lockShort')}
          </text>
        </svg>
      ) : (
        <div className="flex h-full items-center justify-center text-xs text-white/30">{t('bvb.chartWaiting')}</div>
      )}
    </div>
  );
};

export default RoundChart;
